import { supabase } from './supabase';

export async function sendNotification(userIds, title, message, type) {
  if (!userIds || userIds.length === 0) return;
  const records = userIds.map(uid => ({ user_id: uid, title, message, type, read: false }));
  await supabase.from('notifications').insert(records);
}

export async function getAdminIds() {
  const { data } = await supabase.from('users').select('id').eq('role', 'admin').eq('is_active', true);
  return (data || []).map(u => u.id);
}

export async function getBranchManagerIds(branchId) {
  if (!branchId) return [];
  const { data } = await supabase.from('users').select('id').eq('role', 'manager').eq('branch_id', branchId).eq('is_active', true);
  return (data || []).map(u => u.id);
}

export async function getAdminEmails() {
  const { data } = await supabase.from('users').select('email').eq('role', 'admin').eq('is_active', true);
  return (data || []).map(u => u.email).filter(Boolean);
}

export async function getBranchManagerEmails(branchId) {
  if (!branchId) return [];
  const { data } = await supabase.from('users').select('email').eq('role', 'manager').eq('branch_id', branchId).eq('is_active', true);
  return (data || []).map(u => u.email).filter(Boolean);
}

export async function sendEmail(to, subject, html) {
  if (!to || (Array.isArray(to) && to.length === 0)) return;
  if (process.env.NODE_ENV !== 'production') {
    console.log('DEV MODE — email suppressed. Would have sent to:', to, '| Subject:', subject);
    return;
  }
  try {
    const response = await fetch('/.netlify/functions/send-email', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ to, subject, html }),
    });
    if (!response.ok) {
      console.error('Email send failed:', await response.text());
    }
  } catch (err) {
    console.error('Email send error:', err);
  }
}

// ── SHARED TEMPLATE HELPERS ───────────────────────────────

const APP_URL = 'https://dr-signs-tools.pages.dev';

function emailWrapper({ accentColor, icon, eventLabel, bodyContent }) {
  const timestamp = new Date().toLocaleString('en-US', {
    month: 'short', day: 'numeric', year: 'numeric',
    hour: 'numeric', minute: '2-digit', hour12: true,
  });

  return `
<!DOCTYPE html>
<html lang="en">
<head>
  <meta charset="UTF-8" />
  <meta name="viewport" content="width=device-width, initial-scale=1.0" />
  <title>D&amp;R Signs Tool Inventory</title>
</head>
<body style="margin:0;padding:0;background:#F7F7F7;font-family:'Helvetica Neue',Helvetica,Arial,sans-serif;">
  <table width="100%" cellpadding="0" cellspacing="0" style="background:#F7F7F7;padding:32px 16px;">
    <tr>
      <td align="center">
        <table width="100%" cellpadding="0" cellspacing="0" style="max-width:600px;">

          <!-- HEADER -->
          <tr>
            <td style="background:#0F0F0F;border-radius:10px 10px 0 0;padding:24px 32px;border-bottom:3px solid #39D353;">
              <table width="100%" cellpadding="0" cellspacing="0">
                <tr>
                  <td>
                    <span style="font-size:20px;font-weight:700;color:#39D353;letter-spacing:1px;text-transform:uppercase;">D&amp;R Signs</span>
                    <span style="font-size:12px;color:#9A9A9A;letter-spacing:2px;text-transform:uppercase;margin-left:10px;">Tool Inventory</span>
                  </td>
                </tr>
              </table>
            </td>
          </tr>

          <!-- EVENT STRIP -->
          <tr>
            <td style="background:${accentColor};padding:16px 32px;">
              <span style="font-size:16px;font-weight:700;color:#ffffff;text-transform:uppercase;letter-spacing:0.5px;">
                ${icon}&nbsp;&nbsp;${eventLabel}
              </span>
            </td>
          </tr>

          <!-- BODY -->
          <tr>
            <td style="background:#ffffff;padding:32px;border-left:1px solid #E8E8E8;border-right:1px solid #E8E8E8;">
              ${bodyContent}
            </td>
          </tr>

          <!-- CTA -->
          <tr>
            <td style="background:#ffffff;padding:0 32px 32px;border-left:1px solid #E8E8E8;border-right:1px solid #E8E8E8;text-align:center;">
              <a href="${APP_URL}"
                style="display:inline-block;background:#39D353;color:#0F0F0F;font-size:13px;font-weight:700;text-transform:uppercase;letter-spacing:1px;padding:12px 28px;border-radius:6px;text-decoration:none;">
                Open Tool Inventory →
              </a>
            </td>
          </tr>

          <!-- FOOTER -->
          <tr>
            <td style="background:#F0F0F0;border-radius:0 0 10px 10px;padding:16px 32px;border:1px solid #E8E8E8;border-top:none;">
              <table width="100%" cellpadding="0" cellspacing="0">
                <tr>
                  <td style="font-size:11px;color:#9A9A9A;">
                    D&amp;R Signs Tool Inventory System &nbsp;·&nbsp; ${timestamp}
                  </td>
                  <td align="right" style="font-size:11px;color:#9A9A9A;">
                    Do not reply to this email
                  </td>
                </tr>
                <tr>
                  <td colspan="2" style="font-size:11px;color:#C0C0C0;padding-top:4px;">
                    &copy; 2026 D&amp;R Signs
                  </td>
                </tr>
              </table>
            </td>
          </tr>

        </table>
      </td>
    </tr>
  </table>
</body>
</html>
  `;
}

function detailRow(label, value) {
  if (!value) return '';
  return `
    <tr>
      <td style="padding:8px 0;border-bottom:1px solid #F0F0F0;width:140px;vertical-align:top;">
        <span style="font-size:11px;font-weight:700;color:#9A9A9A;text-transform:uppercase;letter-spacing:0.8px;">${label}</span>
      </td>
      <td style="padding:8px 0 8px 16px;border-bottom:1px solid #F0F0F0;vertical-align:top;">
        <span style="font-size:14px;color:#0F0F0F;">${value}</span>
      </td>
    </tr>
  `;
}

function detailTable(rows) {
  return `
    <table width="100%" cellpadding="0" cellspacing="0" style="margin-bottom:24px;">
      ${rows}
    </table>
  `;
}

// ── EMAIL TEMPLATES ───────────────────────────────────────

export function emailToolTaggedOut(toolName, branch, reason, taggedBy, station, serialNumber) {
  const rows = detailTable(
    detailRow('Tool', toolName) +
    detailRow('Branch', branch) +
    detailRow('Station / truck', station) +
    detailRow('Serial number', serialNumber) +
    detailRow('Reason', reason) +
    detailRow('Tagged out by', taggedBy)
  );

  const body = `
    <p style="font-size:15px;color:#0F0F0F;margin:0 0 20px;">
      A tool has been tagged out of service and requires attention. Review the tag-out log and assign it to repair or schedule an assessment.
    </p>
    ${rows}
    <div style="background:#FFF3E0;border-left:4px solid #F5A623;border-radius:4px;padding:12px 16px;font-size:13px;color:#B36F00;">
      Log in to the Tool Inventory to manage this tag-out, update the status, or initiate a repair.
    </div>
  `;

  return emailWrapper({
    accentColor: '#C62828',
    icon: '⚠️',
    eventLabel: 'Tool Tagged Out',
    bodyContent: body,
  });
}

export function emailSpareRequestSubmitted(toolName, branchName, managerName) {
  const rows = detailTable(
    detailRow('Tool requested', toolName) +
    detailRow('Requesting branch', branchName) +
    detailRow('Requested by', managerName)
  );

  const body = `
    <p style="font-size:15px;color:#0F0F0F;margin:0 0 20px;">
      A spare tool request has been submitted and is waiting for your review. Log in to approve or deny the request.
    </p>
    ${rows}
    <div style="background:#E3F2FD;border-left:4px solid #1976D2;border-radius:4px;padding:12px 16px;font-size:13px;color:#1565C0;">
      Once approved, the tool will be prepared for shipment from the Dallas spare pool.
    </div>
  `;

  return emailWrapper({
    accentColor: '#E65100',
    icon: '📋',
    eventLabel: 'New Spare Request',
    bodyContent: body,
  });
}

export function emailToolShipped(toolName, branchName, shippedBy) {
  const rows = detailTable(
    detailRow('Tool', toolName) +
    detailRow('Shipped to', branchName) +
    detailRow('Shipped by', shippedBy)
  );

  const body = `
    <p style="font-size:15px;color:#0F0F0F;margin:0 0 20px;">
      A tool has been shipped to your branch from the Dallas spare pool. Please confirm receipt in the system when it arrives.
    </p>
    ${rows}
    <div style="background:#E8F5E9;border-left:4px solid #39D353;border-radius:4px;padding:12px 16px;font-size:13px;color:#2E7D32;">
      Log in to Spare Requests and click <strong>Confirm receipt</strong> when the tool arrives at your branch.
    </div>
  `;

  return emailWrapper({
    accentColor: '#1565C0',
    icon: '📦',
    eventLabel: 'Tool Shipped',
    bodyContent: body,
  });
}

export function emailInspectionDiscrepancy(stationTruck, branch, discrepancies, notes, conductedBy) {
  const rows = detailTable(
    detailRow('Station / truck', stationTruck) +
    detailRow('Branch', branch) +
    detailRow('Discrepancies found', String(discrepancies)) +
    detailRow('Conducted by', conductedBy) +
    detailRow('Notes', notes)
  );

  const body = `
    <p style="font-size:15px;color:#0F0F0F;margin:0 0 20px;">
      An inspection has been logged with discrepancies that require your attention. Review the details below and follow up as needed.
    </p>
    ${rows}
    <div style="background:#FFEBEE;border-left:4px solid #C62828;border-radius:4px;padding:12px 16px;font-size:13px;color:#C62828;">
      Log in to the Inspections page to review the full inspection record and take action on any flagged items.
    </div>
  `;

  return emailWrapper({
    accentColor: '#C62828',
    icon: '🔍',
    eventLabel: 'Inspection Discrepancy',
    bodyContent: body,
  });
}

export function emailBranchSpareRequest(toolName, branch, department, station, requestedBy) {
  const rows = detailTable(
    detailRow('Tool needed', toolName) +
    detailRow('Branch', branch) +
    detailRow('Department', department) +
    detailRow('Station / truck', station) +
    detailRow('Requested by', requestedBy)
  );

  const body = `
    <p style="font-size:15px;color:#0F0F0F;margin:0 0 20px;">
      A team member has tagged out a tool and is requesting a replacement from the branch spare buffer. Review availability and assign a spare to their station.
    </p>
    ${rows}
    <div style="background:#FFF3E0;border-left:4px solid #F5A623;border-radius:4px;padding:12px 16px;font-size:13px;color:#B36F00;">
      If no branch spare is available, submit a request to the Dallas spare pool to replenish your buffer.
    </div>
  `;

  return emailWrapper({
    accentColor: '#E65100',
    icon: '🔄',
    eventLabel: 'Branch Spare Requested',
    bodyContent: body,
  });
}